'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// 3rd party
import React, { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router'

// walrus
import useLoadingState from './use-loading-state'
import { useNavigate } from 'react-router-dom'
import Box, { BoxProps } from '@components/kit/box'
import { Column, Row, ColumnProps } from '@components/kit/grid'
import { useUserStore } from '@store/User'
import { IUserInfo } from '@store/User/types'
import Loading from '@components/kit/loading'
import { HeaderBackButton } from '@components/kit/header'
import Tabs, { Tab } from '@components/kit/tabs'
import Pressable from '@components/kit/pressable'
import Text from '@components/kit/text'

// lib
import AppShell from '@components/ui/app-shell'
import Profile from './profile'
import Provider from './provider'
import MemberList from './member-list'
import SymptomLogHistory from './symptom-log-history'
import PollsHistory from './polls-history.web'
import ClinicalWork from './clinical-work/clinical-work'
import ChatRoom from './room/chat-room'
import IncomingMessageToast from './incoming-message-toast'
import NextMemberButton from './next-member-button'
import { SHOW_QUEUE_TAB } from './queue-experiment'
import { useChatStore } from '@store/Chat'
import { useAuthStore } from '@store/Auth'
import { useNeedsAttentionStore } from '@store/NeedsAttention'
import { ENV } from '@lib/env'
import { MaximizeIcon, MinimizeIcon } from '@components/kit/icons'
import HealthTracking from './health-tracking'
import Nutrition from './nutrition'

/* -----------------------------------------------------------------------------
 * ColumnHeader
 *
 * common's Header splits into two 50% halves, and the right one, painted last,
 * swallows clicks on any tab past the middle of the column. Here the tabs take
 * the room they need, clipped only when the column is too narrow, and the
 * maximize button keeps just its own width.
 * -------------------------------------------------------------------------- */

type ColumnHeaderProps = BoxProps & {
  headerLeft: React.ReactNode
  headerRight: React.ReactNode
}

function ColumnHeader({ headerLeft, headerRight, ...rest }: ColumnHeaderProps) {
  return (
    <Box
      alignItems='center'
      borderBottomWidth={1}
      borderColor='shade03'
      flexDirection='row'
      height={50}
      {...rest}
    >
      <Box
        flex='1 1 auto'
        flexDirection='row'
        style={{ minWidth: 0, overflow: 'hidden' }}
      >
        {headerLeft}
      </Box>
      <Box flex='0 0 auto' flexDirection='row'>
        {headerRight}
      </Box>
    </Box>
  )
}

enum PageSection {
  NONE = 0,
  PROFILE = 1,
  EVENTS = 2,
  TOOLS = 3
}

/* -----------------------------------------------------------------------------
 * Chat
 * -------------------------------------------------------------------------- */

const MEMBER_LIST_WIDTH = 480
const PROFILE_WIDTH = '35%'
const EVENT_WIDTH = '35%'
const TOOLS_WIDTH = '30%'
const MAX_WIDTH = '46%' // the widened one
const MIN_WIDTH = '27%' // the other two should shink to this size

// web-only: eases the columns into their new widths when one is maximized or
// restored, with the same timing as the AppShell rail resize. react-native's
// style types do not know `transition*`; react-native-web passes it through
const COLUMN_TRANSITION = {
  transitionDuration: '200ms',
  transitionProperty: 'width',
  transitionTimingFunction: 'ease-in-out'
}

/* -----------------------------------------------------------------------------
 * ChatContext
 * -------------------------------------------------------------------------- */

export interface ChatContext {
  userId: number
  roomId?: number
  // fetched once here: four consumers below used to request it on their own
  userInfo: IUserInfo | null
  refreshUserInfo: () => Promise<IUserInfo | null>
  activeTab: ChatTab
  setActiveTab: React.Dispatch<React.SetStateAction<ChatTab>>
  // the selected symptom's id: a log group is now a symptom
  selectedLogGroup: number
  setSelectedLogGroup: React.Dispatch<React.SetStateAction<number>>
  setIsEventOpened: React.Dispatch<React.SetStateAction<boolean>>
  // the zone moment formats in; member info sets it from the member's settings
  memberTimezone: string | null
  setMemberTimezone: React.Dispatch<React.SetStateAction<string | null>>
}

export enum ProfileTab {
  PERSONAL,
  PROVIDER
}

export enum ClinicalTab {
  CLINICAL_WORK,
  HEALTH_TRACKING,
  NUTRITION
}

export enum ChatTab {
  ROOM,
  TRACKING,
  POLLS
}

export const ChatContext = React.createContext<ChatContext>(null)

/* -----------------------------------------------------------------------------
 * Chat
 * -------------------------------------------------------------------------- */

function Chat() {
  const [selectedSection, setSelectedSection] = useState(PageSection.NONE)
  const [currentProfileWidth, setCurrentProfileWidth] = useState(PROFILE_WIDTH)
  const [currentEventWidth, setCurrentEventWidth] = useState(EVENT_WIDTH)
  const [currentToolsWidth, setCurrentToolsWidth] = useState(TOOLS_WIDTH)
  const { chatId, roomId: roomIdParam } = useParams()
  const professionalId = useAuthStore(state => state.userId)
  const accessToken = useAuthStore(state => state.accessToken)
  const { getUserInfo } = useUserStore.getState()
  const [userInfo, setUserInfo] = useState<IUserInfo | null>(null)
  // set when the member's info request came back empty, which used to leave
  // the chat on a spinner for good
  const [isUserInfoMissing, setIsUserInfoMissing] = useState(false)
  const [memberTimezone, setMemberTimezone] = useState<string | null>(null)
  const hasSelectedUser = Boolean(userInfo)
  const showRoom = !!roomIdParam
  const parsedChatId = chatId ? parseInt(chatId, 10) : NaN
  const roomId = useChatStore(state => state.getMemberListRoomId(parsedChatId))
  const navigate = useNavigate()

  const refreshUserInfo = useCallback(async () => {
    const info = await getUserInfo(parsedChatId)
    setUserInfo(info)
    return info
  }, [getUserInfo, parsedChatId])

  const [activeProfileTab, setActiveProfileTab] = useState(ProfileTab.PERSONAL)
  const [activeClinicalTab, setActiveClinicalTab] = useState(
    ClinicalTab.CLINICAL_WORK
  )
  const [activeChatTab, setActiveChatTab] = useState(ChatTab.ROOM)
  const [selectedLogGroup, setSelectedLogGroup] = useState<number>()

  const [isMemberListOpened, setIsMemberListOpened] = useState(true)
  const [isClinicalWorkOpened, setIsClinicalWorkOpened] = useState(false)

  const navZindex = chatId ? 0 : 99
  const contentZindex = chatId ? 99 : 0
  const profileZIndex = showRoom || isClinicalWorkOpened ? 0 : 99
  const clinicalWorkZIndex = isClinicalWorkOpened ? 99 : 0
  const roomZIndex = showRoom ? 99 : 0

  const { isLoaded } = useLoadingState(async () => {
    if (!accessToken) {
      return
    }

    const { fetchMemberList } = useChatStore.getState()
    await fetchMemberList({
      reset: true,
      professionalUserId: professionalId
    })
  }, [chatId, professionalId, accessToken])

  // the list holds only the pages loaded so far, so a member opened by URL may
  // not be in it: look their room up once the list is in
  useEffect(() => {
    if (!isLoaded || !chatId || roomId !== undefined) {
      return
    }

    useChatStore.getState().fetchMemberRoomId(parsedChatId)
  }, [chatId, isLoaded, parsedChatId, roomId])

  // opening a member counts for the needs-attention queue whatever the way in;
  // the queue is read again when the tab comes back, as members write in and
  // colleagues resolve them meanwhile
  useEffect(() => {
    if (!chatId || !professionalId) {
      return
    }
    const { loadNextMember, markMemberOpened } =
      useNeedsAttentionStore.getState()
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadNextMember(parsedChatId, professionalId)
      }
    }

    markMemberOpened(parsedChatId)
    loadNextMember(parsedChatId, professionalId)
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () =>
      document.removeEventListener('visibilitychange', onVisibilityChange)
  }, [chatId, parsedChatId, professionalId])

  const toggleMemberList = () => {
    setIsMemberListOpened(prevState => !prevState)
  }

  // the rail animates over this area, so anything overlapping it gets put away
  // before the width changes: the member list, and any maximized column
  const collapseOverlays = () => {
    setIsMemberListOpened(false)
    selectColumn(PageSection.NONE)
  }

  // Reset the selected section when the chatId changes
  useEffect(() => {
    setUserInfo(null)
    setIsUserInfoMissing(false)
    setActiveChatTab(ChatTab.ROOM)
    setSelectedLogGroup(null)
    if (chatId) {
      setIsMemberListOpened(false)
      setIsClinicalWorkOpened(false)
    }
  }, [chatId])

  const selectColumn = (section: PageSection) => {
    // let newSection = PageSection.NONE
    // if (section !== selectedSection) {
    //   newSection = section
    // }
    if (section === selectedSection) {
      return
    }
    setSelectedSection(section)
    switch (section) {
      case PageSection.PROFILE:
        setCurrentProfileWidth(MAX_WIDTH)
        setCurrentEventWidth(MIN_WIDTH)
        setCurrentToolsWidth(MIN_WIDTH)
        break
      case PageSection.EVENTS:
        setCurrentProfileWidth(MIN_WIDTH)
        setCurrentEventWidth(MAX_WIDTH)
        setCurrentToolsWidth(MIN_WIDTH)
        break
      case PageSection.TOOLS:
        setCurrentProfileWidth(MIN_WIDTH)
        setCurrentEventWidth(MIN_WIDTH)
        setCurrentToolsWidth(MAX_WIDTH)
        break
      default:
        setCurrentProfileWidth(PROFILE_WIDTH)
        setCurrentEventWidth(EVENT_WIDTH)
        setCurrentToolsWidth(TOOLS_WIDTH)
        break
    }
  }

  const MinimizeButton = ({ isMaximized, sectionToMaximize }) => {
    return (
      <Pressable
        display={{
          desktop: 'block',
          mobile: 'none'
        }}
        mr='12'
        onPress={() =>
          selectColumn(isMaximized ? PageSection.NONE : sectionToMaximize)
        }
      >
        {isMaximized ? (
          <MinimizeIcon fill='newPurple' />
        ) : (
          <MaximizeIcon fill='newMutedPurple' />
        )}
      </Pressable>
    )
  }

  // the only place the member's info is fetched; everything below reads it
  // off the context
  useEffect(() => {
    if (!chatId || hasSelectedUser) {
      return
    }

    let isCurrent = true
    refreshUserInfo().then(info => {
      if (isCurrent) {
        setIsUserInfoMissing(!info)
      }
    })

    return () => {
      isCurrent = false
    }
  }, [chatId, hasSelectedUser, refreshUserInfo])

  return (
    <ChatContext.Provider
      value={{
        roomId,
        userId: parsedChatId,
        userInfo,
        refreshUserInfo,
        activeTab: activeChatTab,
        setActiveTab: setActiveChatTab,
        selectedLogGroup,
        setSelectedLogGroup,
        setIsEventOpened: setIsClinicalWorkOpened,
        memberTimezone,
        setMemberTimezone
      }}
    >
      <AppShell
        activePage='chat'
        defaultCompact
        fullBleed
        key={'chatPage' + chatId}
        label='Chat'
        onActivePagePress={toggleMemberList}
        onBeforeNavChange={collapseOverlays}
      >
        <IncomingMessageToast />
        {chatId && SHOW_QUEUE_TAB && <NextMemberButton userId={parsedChatId} />}
        <Row position='relative' height='100%'>
          {(isMemberListOpened || ENV['device_type'] === 'mobile') && (
            <Box
              bg='proSurface'
              borderColor='proBorder'
              borderRightWidth={{ tablet: 1 }}
              borderStyle='solid'
              bottom={0}
              boxShadow='proDrawer'
              justifyContent='center'
              left={0}
              position={{ mobile: 'fixed', tablet: 'absolute' }}
              top={0}
              width={{ mobile: '100%', tablet: MEMBER_LIST_WIDTH }}
              zIndex={{ mobile: navZindex, tablet: 100 }}
            >
              {accessToken && isLoaded ? <MemberList /> : <Loading />}
            </Box>
          )}
          <Row
            position={{ mobile: 'fixed', tablet: 'relative' }}
            top={0}
            bottom={0}
            left={0}
            zIndex={contentZindex}
            width={'100%'}
            flex='1 1 auto'
            bg='proCanvas'
          >
            {!isLoaded || !hasSelectedUser ? (
              <Box flex='1 1 auto' justifyContent='center'>
                {chatId && isUserInfoMissing ? (
                  <Text
                    font='pro.text.13.regular'
                    textAlign='center'
                    textColor='proTextMuted'
                  >
                    This member could not be loaded.
                  </Text>
                ) : (
                  chatId && <Loading />
                )}
              </Box>
            ) : (
              <React.Fragment>
                <Column
                  position={{ mobile: 'absolute', desktop: 'relative' }}
                  top={0}
                  bottom={0}
                  left={0}
                  height='100%'
                  zIndex={profileZIndex}
                  width={{
                    mobile: '100%',
                    desktop: !!selectedSection
                      ? currentProfileWidth
                      : PROFILE_WIDTH
                  }}
                  flex='1 1 auto'
                  backgroundColor='shade01b'
                  style={COLUMN_TRANSITION as ColumnProps['style']}
                >
                  <ColumnHeader
                    bg='white'
                    headerLeft={
                      <React.Fragment>
                        <HeaderBackButton
                          preventDefault
                          display={{ tablet: 'none' }}
                          alignSelf='flex-start'
                          ml='-12'
                          onPress={() => navigate('/chat')}
                        />

                        <Tabs marginLeft='20'>
                          <Tab
                            onPress={() =>
                              setActiveProfileTab(ProfileTab.PERSONAL)
                            }
                            active={activeProfileTab === ProfileTab.PERSONAL}
                          >
                            Personal
                          </Tab>
                          <Tab
                            onPress={() =>
                              setActiveProfileTab(ProfileTab.PROVIDER)
                            }
                            active={activeProfileTab === ProfileTab.PROVIDER}
                          >
                            Provider & Pharmacy
                          </Tab>
                        </Tabs>
                      </React.Fragment>
                    }
                    headerRight={
                      <MinimizeButton
                        sectionToMaximize={PageSection.PROFILE}
                        isMaximized={selectedSection === PageSection.PROFILE}
                      />
                    }
                  />
                  <Box paddingRight='08' flex='1'>
                    {activeProfileTab === ProfileTab.PERSONAL && (
                      <Profile
                        openEvents={() => setIsClinicalWorkOpened(true)}
                      />
                    )}
                    {activeProfileTab === ProfileTab.PROVIDER && <Provider />}
                  </Box>
                </Column>
                <Column
                  position={{ mobile: 'absolute', desktop: 'relative' }}
                  top={0}
                  bottom={0}
                  left={0}
                  height='100%'
                  zIndex={clinicalWorkZIndex}
                  width={{
                    mobile: '100%',
                    desktop: !!selectedSection ? currentEventWidth : EVENT_WIDTH
                  }}
                  flex='1 1 auto'
                  backgroundColor='shade01b'
                  borderColor='walrusBlue100'
                  style={{
                    borderLeftWidth: 1,
                    ...COLUMN_TRANSITION
                  }}
                >
                  <ColumnHeader
                    bg='white'
                    headerLeft={
                      <React.Fragment>
                        <HeaderBackButton
                          preventDefault
                          display={{ desktop: 'none' }}
                          alignSelf='flex-start'
                          onPress={() => setIsClinicalWorkOpened(false)}
                        />
                        <Tabs marginLeft='20'>
                          <Tab
                            onPress={() =>
                              setActiveClinicalTab(ClinicalTab.CLINICAL_WORK)
                            }
                            active={
                              activeClinicalTab === ClinicalTab.CLINICAL_WORK
                            }
                          >
                            Clinical Work
                          </Tab>
                          <Tab
                            onPress={() =>
                              setActiveClinicalTab(ClinicalTab.HEALTH_TRACKING)
                            }
                            active={
                              activeClinicalTab === ClinicalTab.HEALTH_TRACKING
                            }
                          >
                            Health Tracking
                          </Tab>
                          <Tab
                            onPress={() =>
                              setActiveClinicalTab(ClinicalTab.NUTRITION)
                            }
                            active={activeClinicalTab === ClinicalTab.NUTRITION}
                          >
                            Nutrition
                          </Tab>
                        </Tabs>
                      </React.Fragment>
                    }
                    headerRight={
                      <MinimizeButton
                        sectionToMaximize={PageSection.EVENTS}
                        isMaximized={selectedSection === PageSection.EVENTS}
                      />
                    }
                  />
                  <Box paddingRight='08' flex='1'>
                    {activeClinicalTab === ClinicalTab.CLINICAL_WORK && (
                      <ClinicalWork
                        closeClinicalWork={() => setIsClinicalWorkOpened(false)}
                      />
                    )}
                    {activeClinicalTab === ClinicalTab.HEALTH_TRACKING && (
                      <HealthTracking />
                    )}
                    {activeClinicalTab === ClinicalTab.NUTRITION && (
                      <Nutrition />
                    )}
                  </Box>
                </Column>
                <Column
                  width={{
                    mobile: '100%',
                    desktop: !!selectedSection ? currentToolsWidth : TOOLS_WIDTH
                  }}
                  position={{ mobile: 'absolute', desktop: 'relative' }}
                  top={0}
                  bottom={0}
                  left={0}
                  height='100%'
                  zIndex={roomZIndex}
                  bg='white'
                  boxShadow='panel'
                  borderColor='walrusBlue100'
                  style={{
                    borderLeftWidth: 1,
                    ...COLUMN_TRANSITION
                  }}
                >
                  <ColumnHeader
                    headerLeft={
                      <React.Fragment>
                        <HeaderBackButton
                          display={{ desktop: 'none' }}
                          preventDefault
                          onPress={() =>
                            navigate(chatId ? `/chat/${chatId}` : '/chat')
                          }
                        />
                        <Tabs marginLeft='20'>
                          <Tab
                            onPress={() => setActiveChatTab(ChatTab.ROOM)}
                            active={activeChatTab === ChatTab.ROOM}
                          >
                            Chat
                          </Tab>
                          <Tab
                            onPress={() => setActiveChatTab(ChatTab.TRACKING)}
                            active={activeChatTab === ChatTab.TRACKING}
                          >
                            Symptoms
                          </Tab>
                          <Tab
                            onPress={() => setActiveChatTab(ChatTab.POLLS)}
                            active={activeChatTab === ChatTab.POLLS}
                          >
                            Polls
                          </Tab>
                        </Tabs>
                      </React.Fragment>
                    }
                    headerRight={
                      <MinimizeButton
                        sectionToMaximize={PageSection.TOOLS}
                        isMaximized={selectedSection === PageSection.TOOLS}
                      />
                    }
                  />
                  <Box
                    paddingRight='08'
                    flex='1 1 0'
                    flexDirection='column'
                    style={{ minHeight: 0, overflow: 'hidden' }}
                  >
                    {activeChatTab === ChatTab.ROOM && (
                      <ChatRoom roomId={roomId} />
                    )}
                    {activeChatTab === ChatTab.TRACKING && (
                      <SymptomLogHistory />
                    )}
                    {activeChatTab === ChatTab.POLLS && <PollsHistory />}
                  </Box>
                </Column>
              </React.Fragment>
            )}
          </Row>
        </Row>
      </AppShell>
    </ChatContext.Provider>
  )
}

export default Chat
